import React, { Component } from "react";
import { Configuration, getDataTable } from "../Configuration";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { Modal, Spin } from "antd";
import "antd/dist/antd.css";

/* Values are concatenated into an EXEC string by getDataTable, and a tracker
   link can legitimately contain quotes. Doubling every single quote is what
   stops one closing the literal. Applied to every interpolated value. */
const q = (v) =>
  v === null || v === undefined ? "" : String(v).replace(/'/g, "''");

export default class frmTracker extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loginId: Configuration.USER_ID,
      loading: false,
      rows: [],

      // add / edit dialog
      modalOpen: false,
      editingId: 0,
      name: "",
      link: "",
      saving: false,
    };
  }

  componentDidMount() {
    this.loadRows();
  }

  loadRows = async () => {
    this.setState({ loading: true });
    const dt = await getDataTable(
      "EXEC dbo.TrackerMaster_List @loginID = " + this.state.loginId
    );
    this.setState({ rows: Array.isArray(dt) ? dt : [], loading: false });
  };

  // ── Add / edit ────────────────────────────────────────────────────────────

  openAdd = () =>
    this.setState({ modalOpen: true, editingId: 0, name: "", link: "" });

  openEdit = (r) =>
    this.setState({
      modalOpen: true,
      editingId: r.TrackerID,
      name: r.TrackerName || "",
      link: r.TrackerLink || "",
    });

  closeModal = () => this.setState({ modalOpen: false, saving: false });

  save = async () => {
    const { loginId, editingId, name, link } = this.state;

    if (!name.trim()) {
      Swal.fire({ icon: "info", title: "Please enter a tracker name", timer: 1700, showConfirmButton: false });
      return;
    }
    if (!link.trim()) {
      Swal.fire({ icon: "info", title: "Please enter a link", timer: 1700, showConfirmButton: false });
      return;
    }

    this.setState({ saving: true });
    const res = await getDataTable(
      "EXEC dbo.TrackerMaster_Save @loginID = " + loginId +
      ", @trackerID = " + (parseInt(editingId, 10) || 0) +
      ", @name = '" + q(name.trim()) + "'" +
      ", @link = '" + q(link.trim()) + "'"
    );
    this.setState({ saving: false });

    // The proc RAISERRORs on a bad link or a tracker outside the user's
    // organisation, and getDataTable turns that into false.
    if (res === false) {
      Swal.fire(
        "Not saved",
        "The link must be a web address (http:// or https://), and the tracker must belong to your organisation.",
        "error"
      );
      return;
    }

    this.setState({ modalOpen: false });
    Swal.fire({
      icon: "success",
      title: editingId ? "Tracker updated" : "Tracker added",
      timer: 1500,
      showConfirmButton: false,
    });
    this.loadRows();
  };

  remove = async (r) => {
    const ok = await Swal.fire({
      title: "Delete this tracker?",
      html: "<b>" + (r.TrackerName || "") + "</b>",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33",
    });
    if (!ok.isConfirmed) return;

    this.setState({ loading: true });
    await getDataTable(
      "EXEC dbo.TrackerMaster_Delete @loginID = " + this.state.loginId +
      ", @trackerID = " + r.TrackerID
    );
    this.loadRows();
  };

  /* Open in a new tab. noopener/noreferrer matter here: the target is a URL a
     user typed, so the opened page must not get a handle back to this one. */
  openTracker = (r) => {
    const url = (r.TrackerLink || "").trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) {
      Swal.fire("Cannot open", "This tracker's link is not a valid web address.", "error");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // ── Render ────────────────────────────────────────────────────────────────

  render() {
    const { loading, rows, modalOpen, editingId, name, link, saving } = this.state;

    return (
      <div className="content-wrapper">
        {loading && (
          <div className="loader" style={{ display: "block" }}>
            <div className="loader-item"><Spin /></div>
          </div>
        )}

        <section className="content">
          <div className="box box-success">
            {/* Header — same breadcrumb + title + right-aligned action as the
                other tool pages */}
            <div className="box-header with-border" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "12px 20px" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, color: "#aaa", marginBottom: 3 }}>
                  Dashboard &gt; Tools &gt; Tracker
                </div>
                <h3 className="box-title" style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                  Tracker
                </h3>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4, flexWrap: "wrap" }}>
                <button className="btn btn-sm btn-default" onClick={this.openAdd}>
                  ⊕ Add Tracker
                </button>
              </div>
            </div>

            {/* How-to banner, matching Master Data Filler */}
            <div style={{ background: "#fffbe6", borderBottom: "1px solid #ffe58f", padding: "8px 16px", fontSize: 12, color: "#666" }}>
              <b>How to use:</b> Use <b>⊕ Add Tracker</b> to save a name and its link →
              click a <b>tracker name</b> in the list to open it in a new tab →
              use <b>Edit</b> to change a name or link.
            </div>

            <div className="box-body">
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th style={{ width: 80 }}>SR NO.</th>
                      <th>TRACKER NAME</th>
                      <th style={{ width: 190 }}>LAST UPDATED</th>
                      <th style={{ width: 150 }}>ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan="4" style={{ textAlign: "center", color: "#999", padding: 22 }}>
                          No trackers yet. Use <b>⊕ Add Tracker</b> to create one.
                        </td>
                      </tr>
                    )}
                    {rows.map((r, i) => (
                      <tr key={r.TrackerID}>
                        <td>{i + 1}</td>
                        <td>
                          {/* The name is the link — clicking opens the target */}
                          <a
                            onClick={() => this.openTracker(r)}
                            title={r.TrackerLink}
                            style={{
                              cursor: "pointer",
                              fontWeight: 600,
                              color: "var(--brand-teal, #3D9970)",
                            }}
                          >
                            {r.TrackerName}
                          </a>
                        </td>
                        <td style={{ fontSize: 11, color: "#888" }}>
                          {r.UpdatedOn ? String(r.UpdatedOn).substring(0, 10) : ""}
                          {r.UpdatedByName ? " · " + r.UpdatedByName : ""}
                        </td>
                        <td>
                          <button className="btn btn-xs btn-default" style={{ marginRight: 4 }}
                            onClick={() => this.openEdit(r)}>
                            Edit
                          </button>
                          <button className="btn btn-xs btn-danger"
                            onClick={() => this.remove(r)}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* ── Add / edit dialog ─────────────────────────────────────────── */}
        <Modal
          title={editingId ? "Edit Tracker" : "Add Tracker"}
          visible={modalOpen}
          onCancel={this.closeModal}
          footer={null}
          width={Math.min(520, window.innerWidth * 0.92)}
          style={{ top: 70 }}
          bodyStyle={{ padding: "16px 20px" }}
          maskClosable={!saving}
          destroyOnClose
        >
          <div className="row">
            <div className="col-xs-12 margintop">
              <label style={{ display: "block", marginBottom: 3 }}>
                Name <span className="vcode">*</span>
              </label>
              <input
                className="form-control"
                value={name}
                placeholder="e.g. Daily Sales"
                autoFocus
                onChange={(e) => this.setState({ name: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") this.save(); }}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-xs-12 margintop">
              <label style={{ display: "block", marginBottom: 3 }}>
                Link <span className="vcode">*</span>
              </label>
              <input
                className="form-control"
                value={link}
                placeholder="https://…"
                onChange={(e) => this.setState({ link: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") this.save(); }}
              />
              <div style={{ fontSize: 11, color: "#888", marginTop: 3 }}>
                A web address. If you leave off <b>https://</b> it will be added for you.
              </div>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
            <button className="btn btn-default" onClick={this.closeModal} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-success" onClick={this.save} disabled={saving}>
              {saving ? "Saving…" : editingId ? "Update Tracker" : "Save Tracker"}
            </button>
          </div>
        </Modal>
      </div>
    );
  }
}
